import { Button, Group, Popover, Stack } from '@mantine/core';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import clsx from 'clsx';
import { type ComponentPropsWithRef, type MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MATH_KEYBOARD_LIFT_PROPS, MathLiveField } from 'shared/components/MathLiveField';

import classes from './InlineMathNodeView.module.scss';
import type { StaticNodeViewProps } from '../../../components/richTexts/RichText/nodeViews';
import { renderStaticMathDisplayHtml } from '../../math/staticMathHtml';

// Math is intrinsically LTR; isolate it so it isn't reordered inside RTL text.
const WRAPPER_STYLE = { display: 'inline-block', direction: 'ltr', unicodeBidi: 'isolate' } as const;

type InlineMathFormulaProps = ComponentPropsWithRef<'span'> & { editable: boolean; latex: string };

/** The rendered formula, shared by the live node view and static rich text. */
function InlineMathFormula({ className, editable, latex, ...spanProps }: InlineMathFormulaProps) {
  const { t } = useTranslation('widgets.mathControl');
  const html = useMemo(() => renderStaticMathDisplayHtml(latex), [latex]);
  return (
    <span
      // `dir="ltr"` forces LTR base direction (UA maps it to direction:ltr +
      // unicode-bidi:isolate) so KaTeX isn't reordered inside RTL (Hebrew) text.
      dir="ltr"
      contentEditable={false}
      // A button only when editable: keyboard-only authors open the editor with Enter/Space. Read-only, it
      // would be a nameless button that does nothing, and a button's children are presentational, which can
      // hide KaTeX's MathML from screen readers.
      role={editable ? 'button' : undefined}
      tabIndex={editable ? 0 : undefined}
      aria-label={editable ? t('edit') : undefined}
      // Light hover hint; the gesture is double-click (single-click selects).
      title={editable ? t('edit') : undefined}
      style={{
        cursor: editable ? 'pointer' : 'default',
        display: 'inline-block',
        direction: 'ltr',
        unicodeBidi: 'isolate',
        borderRadius: 4
      }}
      {...spanProps}
      // The selection ring is drawn from the wrapper's `ProseMirror-selectednode` class (module.scss), so
      // the formula reads as selectable content when a click or a range selection covers it.
      className={clsx(classes.formula, className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/**
 * The formula in static (editor-less) read-only rich text. It is never editable and has no edit popover,
 * since nothing could open it.
 */
export function StaticInlineMath({ node }: Pick<StaticNodeViewProps, 'node'>) {
  return (
    <NodeViewWrapper as="span" style={WRAPPER_STYLE}>
      <InlineMathFormula editable={false} latex={(node.attrs.latex as string) ?? ''} />
    </NodeViewWrapper>
  );
}

/**
 * React node view for the `inlineMath` node. Renders the formula with KaTeX and, when
 * editable, opens a mathlive popover on DOUBLE-click (or Enter/Space when focused) so the
 * formula can be edited live and written back to the node's `latex` attribute.
 *
 * Single-click / drag are intentionally left to ProseMirror: the node is an `atom` +
 * `selectable`, so a plain click selects it and it participates in range selections — exactly
 * like normal content. (Previously a `mousedown`-`preventDefault` + click-to-edit suppressed
 * all of that, so the formula couldn't be selected at all.) The selection ring follows the wrapper's
 * `ProseMirror-selectednode` class.
 */
export function InlineMathNodeView({ editor, getPos, node, updateAttributes }: NodeViewProps) {
  const { t } = useTranslation('widgets.mathControl');
  const latex = (node.attrs.latex as string) ?? '';
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState(latex);

  const openEditor = () => {
    if (!editor.isEditable) return;
    setDraft(latex);
    setOpened(true);
  };

  // Single-click selects the formula. We can't just let the click fall through to ProseMirror:
  // the rendered span is focusable (tabIndex, for keyboard a11y), so a plain click would move DOM
  // focus to the span, leaving the editor's contentEditable root unfocused — the node would show
  // as selected but Backspace/Delete would go to the span and do nothing. So we keep focus in the
  // editor and set the NodeSelection ourselves, so the selected formula can be deleted normally.
  const selectSelf = (event: MouseEvent) => {
    // Only a plain primary-button click selects the node. Modified clicks (Shift to extend a range,
    // Ctrl/Cmd/Alt) fall through to ProseMirror's native selection handling untouched.
    if (!editor.isEditable || event.button !== 0 || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey)
      return;
    event.preventDefault();
    const pos = getPos();
    if (typeof pos === 'number') editor.chain().focus().setNodeSelection(pos).run();
  };

  const save = () => {
    updateAttributes({ latex: draft.trim() });
    setOpened(false);
  };

  return (
    <NodeViewWrapper as="span" style={WRAPPER_STYLE}>
      <Popover
        opened={opened}
        onClose={() => setOpened(false)}
        position="bottom"
        width={360}
        closeOnClickOutside={false}
        trapFocus={false}
      >
        <Popover.Target>
          <InlineMathFormula
            editable={editor.isEditable}
            latex={latex}
            onMouseDown={selectSelf}
            onDoubleClick={openEditor}
            onKeyDown={event => {
              if (editor.isEditable && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                openEditor();
              }
            }}
          />
        </Popover.Target>
        {/* Lift marker: the dropdown is viewport-positioned, so when the math keyboard would cover
            it, the whole dropdown is translated above the keyboard (see virtualKeyboardLift.ts). */}
        <Popover.Dropdown {...MATH_KEYBOARD_LIFT_PROPS}>
          <Stack gap="sm">
            <MathLiveField value={draft} onChange={setDraft} autoFocus />
            {/*
              Act on mousedown (with preventDefault), not click: clicking a button blurs the math
              field, MathLive auto-hides the on-screen keyboard, the floating popover reflows, and the
              button slides out from under the cursor so the `click` never fires. preventDefault also
              keeps focus on the field. onClick stays as the keyboard-activation (Enter/Space)
              fallback; save()/close are idempotent so a stray double-fire is harmless.
            */}
            <Group justify="flex-end" gap="xs">
              <Button
                variant="default"
                size="xs"
                onMouseDown={(event: MouseEvent) => {
                  event.preventDefault();
                  setOpened(false);
                }}
                onClick={() => setOpened(false)}
              >
                {t('cancel')}
              </Button>
              <Button
                size="xs"
                onMouseDown={(event: MouseEvent) => {
                  event.preventDefault();
                  save();
                }}
                onClick={save}
                disabled={!draft.trim()}
              >
                {t('update')}
              </Button>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </NodeViewWrapper>
  );
}
